import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import {
  applyNovaCitations,
  buildNovaAvaPayload,
  createChatSessionKey,
  createNovaConversationId,
  deleteNovaConversations,
  getNovaConversation,
  isNovaStreamAbort,
  listNovaConversations,
  refreshNovaImageUrls,
  saveNovaConversation,
  saveToScrapbookWithPath,
  streamNovaAvaChat,
} from '@/api';
import {
  GDAIChatSource,
  NovaAvaCitation,
  NovaAvaStreamImage,
  NovaChatMessage,
  NovaConversationSummary,
  NovaSaveConversationRequest,
  ScrapbookContentType,
} from '@/types';
import {
  AppInsightsContext,
  TrackEventKey,
  TrackEventName,
  TrackEventType,
  UserContext,
  useNotification,
} from '@/components';

// Shown when the backend hasn't given us an amsUserId to identify the user with —
// most commonly an IP-based identity whose backend account mapping isn't configured,
// since those sign-ins skip the normal SSO exchange that populates it.
const MISSING_USER_ID_MESSAGE =
  "Nova can't identify your account right now, so it can't send your question. " +
  'If you access this site via IP-based access rather than signing in, this usually means ' +
  'the backend account mapping for your organisation is misconfigured — please contact support.';

// Paced reveal of streamed answer text (see revealTick in sendMessage).
// Small, frequent steps (~60fps) read as smooth typing rather than jumpy bursts.
const REVEAL_INTERVAL_MS = 16;
const REVEAL_MIN_CHARS = 2;
const REVEAL_DRAIN_TICKS = 60;

const PUBLISHED_12M_FILTER_KEY = 'nova.published12mFilter';

const conversationsCacheKey = (userId: string) => `nova.conversations.${userId}`;

const readCachedConversations = (userId: string): NovaConversationSummary[] | null => {
  if (!userId) return null;
  try {
    const raw = window.sessionStorage.getItem(conversationsCacheKey(userId));
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const writeCachedConversations = (userId: string, conversations: NovaConversationSummary[]) => {
  if (!userId) return;
  try {
    window.sessionStorage.setItem(conversationsCacheKey(userId), JSON.stringify(conversations));
  } catch {}
};

const updateCachedConversations = (
  userId: string,
  update: (conversations: NovaConversationSummary[]) => NovaConversationSummary[],
) => {
  const cached = readCachedConversations(userId);
  if (cached) writeCachedConversations(userId, update(cached));
};

// Shared send/receive/history logic used by both the floating NovaPanel and the
// full-page Nova landing module, so the two surfaces never diverge in behaviour.
interface UseNovaChatOptions {
  // sessionStorage key under which the active conversation id is kept, so the chat
  // survives navigation and refresh (per tab) until the user starts a new one. Each
  // surface needs its own key (the floating panel and the full page are separate
  // chats). Omit to not persist.
  persistKey?: string;
  // Holds off restoring while the caller is about to seed a chat itself (e.g. from
  // ?q= / ?c=), which must win over the remembered one.
  restoreEnabled?: boolean;
}

export const useNovaChat = ({ persistKey, restoreEnabled = true }: UseNovaChatOptions = {}) => {
  const appInsights = useContext(AppInsightsContext);
  const user = useContext(UserContext);
  const { showNotification } = useNotification();

  const [messages, setMessages] = useState<NovaChatMessage[]>([]);
  const [suggestedQuestions, setSuggestedQuestions] = useState<string[]>([]);
  const [waiting, setWaiting] = useState(false);
  // True for the whole request, unlike `waiting` (which clears on the first token) —
  // lets the UI keep rendering the answer as plain flowing text while it's still
  // arriving, instead of re-deriving the structured headline/sections layout on every
  // token (which flickers as headings resolve differently mid-stream).
  const [streaming, setStreaming] = useState(false);
  const [conversations, setConversations] = useState<NovaConversationSummary[]>([]);
  const [conversationsLoading, setConversationsLoading] = useState(false);
  const [published12mFilter, setPublished12mFilterState] = useState(true);
  useEffect(() => {
    try {
      const saved = window.localStorage.getItem(PUBLISHED_12M_FILTER_KEY);
      if (saved !== null) setPublished12mFilterState(saved === 'true');
    } catch {}
  }, []);
  const setPublished12mFilter = useCallback((value: boolean) => {
    setPublished12mFilterState(value);
    try {
      window.localStorage.setItem(PUBLISHED_12M_FILTER_KEY, String(value));
    } catch {}
  }, []);
  // Filenames of documents uploaded via NovaUploadDialog but not yet sent —
  // attached to the next outgoing message (as file_paths), then cleared.
  const [pendingFilePaths, setPendingFilePaths] = useState<string[]>([]);
  const addPendingFilePaths = useCallback((filePaths: string[]) => {
    setPendingFilePaths((prev) => [...prev, ...filePaths]);
  }, []);
  const removePendingFilePath = useCallback((filePath: string) => {
    setPendingFilePaths((prev) => prev.filter((path) => path !== filePath));
  }, []);
  // conv_id from the Stylus AI createconversation API — doubles as this hook's conversation
  // identifier (used for scrapbook paths and, eventually, real history lookups).
  const conversationId = useRef<string>('');
  // chatsessionkey for the AVA stream — one per conversation, alongside conv_id.
  const chatSessionKey = useRef<string>('');
  // Mirrors `messages` synchronously (React defers when a setState updater actually
  // runs, so reading `messages` right after a setMessages call can't be trusted) —
  // sendMessage needs the exact final transcript the instant streaming ends, to save
  // the conversation without racing a re-render.
  const messagesRef = useRef<NovaChatMessage[]>([]);
  // Belt-and-suspenders against a second send starting while one is still streaming —
  // the UI already disables the composer for the duration, but if a caller ever bypasses
  // that (e.g. a seeded query racing a user submit), two concurrent onToken loops would
  // both append to `messages`, and the older stream's trailing tokens would land on
  // whatever message is now last (usually the newer one), corrupting both turns.
  const isSendingRef = useRef(false);
  // Bumped by startNewConversation/resumeConversation so an in-flight sendMessage
  // from the conversation being left can tell it's been abandoned — without this,
  // its still-arriving tokens/sources and its post-stream auto-save would keep
  // writing into whatever conversation the user has since switched to.
  const generationRef = useRef(0);
  // Per-conversation counter of auto-save attempts, so an older save that resolves
  // after a newer one (network reordering, retry) doesn't clobber the sidebar
  // preview with stale content — only the most recently *initiated* save for a
  // given conversation is allowed to apply its result.
  const saveAttemptRef = useRef(new Map<string, number>());
  // Set once a chat has been restored, seeded, resumed, started or sent to — after
  // that, the remembered conversation must never be restored over it.
  const restoredRef = useRef(false);
  const abortControllerRef = useRef<AbortController | null>(null);

  const abortInFlightStream = useCallback(() => {
    abortControllerRef.current?.abort();
    abortControllerRef.current = null;
  }, []);

  // Shared by sendMessage and file uploads (NovaUploadDialog) so both use the same
  // per-conversation session id, whichever triggers first — uploads need it before
  // any message has been sent, since it doubles as the S3 folder for that conversation.
  const ensureChatSessionKey = useCallback(() => {
    if (!chatSessionKey.current) {
      chatSessionKey.current = createChatSessionKey();
    }
    return chatSessionKey.current;
  }, []);

  const loadConversations = useCallback(async () => {
    const userId = user?.userId || '';
    const cached = readCachedConversations(userId);
    if (cached) {
      setConversations(cached);
      return;
    }
    setConversationsLoading(true);
    try {
      const response = await listNovaConversations(userId);
      setConversations(response.conversations);
      writeCachedConversations(userId, response.conversations);
    } catch (error) {
      console.error('Error loading Nova conversations:', error);
    } finally {
      setConversationsLoading(false);
    }
  }, [user?.userId]);

  // Moves the just-saved conversation to the top of the in-memory list instead of
  // re-fetching the whole history after every turn — a full refetch would flip
  // `conversationsLoading` back on and blank out the sidebar (which already has
  // this data) just to redraw the same rows.
  const upsertConversation = useCallback(
    (chat: NovaSaveConversationRequest) => {
      const upsert = (prev: NovaConversationSummary[]) => {
        const now = new Date(chat.timestamp).toISOString();
        const existing = prev.find((c) => c.conversationId === chat.conversationId);
        const summary: NovaConversationSummary = {
          conversationId: chat.conversationId,
          title: chat.title,
          lastMessagePreview: chat.lastMessagePreview,
          dateCreated: existing?.dateCreated ?? now,
          dateModified: now,
        };
        return [summary, ...prev.filter((c) => c.conversationId !== chat.conversationId)];
      };
      setConversations(upsert);
      updateCachedConversations(user?.userId || '', upsert);
    },
    [user?.userId],
  );

  const sendMessage = useCallback(
    // `extraFilePaths` attaches documents to this one message without going through
    // pendingFilePaths state — needed when a send is fired straight after a hand-off
    // (e.g. Documents' "Ask Nova"), before a state update could be read back here.
    async (query: string, extraFilePaths: string[] = []) => {
      if (!query.trim() || isSendingRef.current) return;
      isSendingRef.current = true;
      restoredRef.current = true;
      const generation = generationRef.current;
      const isStale = () => generationRef.current !== generation;

      setWaiting(true);
      setStreaming(true);
      appInsights.trackCustomEvent(TrackEventName.NOVA, TrackEventKey.QUESTION, TrackEventType.ASKED, user, { query });

      const userMessage: NovaChatMessage = { role: 'user', content: query, timestamp: Date.now() };
      messagesRef.current = [...messagesRef.current, userMessage];
      setMessages((prev) => [...prev, userMessage]);

      if (!user?.amsUserId) {
        const errorMessage: NovaChatMessage = {
          role: 'assistant',
          content: MISSING_USER_ID_MESSAGE,
          timestamp: Date.now(),
        };
        messagesRef.current = [...messagesRef.current, errorMessage];
        setMessages((prev) => [...prev, errorMessage]);
        showNotification(MISSING_USER_ID_MESSAGE, 'error');
        setWaiting(false);
        setStreaming(false);
        isSendingRef.current = false;
        return;
      }

      // Declared outside the try so the catch block below can tell whether the
      // stream broke mid-answer (some content already landed in `messages`) versus
      // failing before anything arrived — those two cases need different UI. Same
      // reason for latestSources/latestImages: a source/image chunk can arrive
      // before the stream then drops, and that data would otherwise be discarded
      // on error since it was never attached to the message. `assistantMessage` is
      // reassigned (not mutated) on every update so messagesRef.current — built from
      // it below — always reflects the exact object last pushed to `messages`.
      let firstTokenReceived = false;
      let latestSources: GDAIChatSource[] | undefined;
      let latestImages: NovaAvaStreamImage[] | undefined;
      let latestCitations: NovaAvaCitation[] | undefined;
      let assistantMessage: NovaChatMessage = { role: 'assistant', content: '', timestamp: Date.now() };
      // Attach whatever's pending, then clear — files apply to this message only.
      const filePaths = Array.from(new Set([...pendingFilePaths, ...extraFilePaths]));
      if (pendingFilePaths.length > 0) setPendingFilePaths([]);
      // The citations event, when sent, is the authoritative source list and numbering —
      // applied over whatever sources/images the stream carried, once the stream has ended
      // (or broken), before they're attached to the message.
      const resolveCitations = () => {
        if (!latestCitations) return;
        const resolved = applyNovaCitations(latestCitations, latestImages);
        latestSources = resolved.sources;
        if (latestImages) latestImages = resolved.images;
      };
      // Paced-reveal state (see revealTick below) — tokens received but not yet shown.
      let pendingText = '';
      let streamFinished = false;
      let revealTimer: ReturnType<typeof setInterval> | undefined;
      let resolveReveal: (() => void) | undefined;
      const stopReveal = () => {
        if (revealTimer) clearInterval(revealTimer);
        revealTimer = undefined;
        resolveReveal?.();
      };
      abortInFlightStream();
      const abortController = new AbortController();
      abortControllerRef.current = abortController;
      abortController.signal.addEventListener('abort', stopReveal, { once: true });

      // Built from messagesRef + the locally tracked assistantMessage, not the
      // `messages` state closure (stale) or a setMessages callback (its timing isn't
      // guaranteed here).
      const saveTurn = () => {
        messagesRef.current = [...messagesRef.current, assistantMessage];
        if (!user?.userId) return;
        // The conversation's title, not this turn's question — otherwise every
        // follow-up would overwrite it with whatever was just asked.
        const title = messagesRef.current.find((m) => m.role === 'user')?.content || query;
        const chatToSave: NovaSaveConversationRequest = {
          conversationId: conversationId.current,
          lastMessagePreview: assistantMessage.content.substring(0, 200),
          title,
          timestamp: assistantMessage.timestamp ?? Date.now(),
          messages: messagesRef.current,
        };
        const convId = chatToSave.conversationId;
        const attempt = (saveAttemptRef.current.get(convId) ?? 0) + 1;
        saveAttemptRef.current.set(convId, attempt);
        saveNovaConversation(user.userId, chatToSave)
          .then((response) => {
            if (!response.saved) {
              console.error('Nova conversation auto-save was rejected by the server:', response);
              return;
            }
            // Only the latest-initiated save for this conversation may update the
            // sidebar — an earlier one resolving late would otherwise overwrite a
            // newer turn's title/preview with stale content.
            if (saveAttemptRef.current.get(convId) !== attempt) return;
            // listConversations is only called once, on mount — every turn after
            // that updates the sidebar locally instead of re-hitting the API.
            upsertConversation(chatToSave);
          })
          .catch((error) => console.error('Error auto-saving Nova conversation:', error));
      };

      const finishStoppedTurn = () => {
        stopReveal();
        pendingText = '';
        if (isStale()) return;
        resolveCitations();
        const hadAssistantMessage = firstTokenReceived;
        const replaceOrAppend = (message: NovaChatMessage) =>
          setMessages((prev) => {
            if (!hadAssistantMessage) return [...prev, message];
            const next = [...prev];
            if (next[next.length - 1]?.role === 'assistant') next[next.length - 1] = message;
            return next;
          });
        if (!assistantMessage.content) {
          replaceOrAppend({ ...assistantMessage, content: '*Response stopped.*' });
          return;
        }
        assistantMessage = {
          ...assistantMessage,
          sources: latestSources ?? assistantMessage.sources,
          images: latestImages ?? assistantMessage.images,
        };
        replaceOrAppend(assistantMessage);
        saveTurn();
      };

      try {
        ensureChatSessionKey();
        if (!conversationId.current) {
          const newConversationId = await createNovaConversationId();
          // The conversation was switched away from while this awaited — the new
          // conversation may have already gotten (or will get) its own id; don't
          // clobber it with one created for the abandoned turn.
          if (isStale()) return;
          conversationId.current = newConversationId;
        }

        const payload = buildNovaAvaPayload({
          query,
          name: user?.name?.trim().split(/\s+/)[0] || '',
          userProfileId: user?.amsUserId || '',
          chatSessionKey: chatSessionKey.current,
          convId: conversationId.current,
          published12mFilter,
          filePaths,
        });

        // Sources/images can arrive before any text token (or, for an images-only
        // answer, before any text token ever arrives at all) — without this, the
        // assistant bubble wouldn't exist yet when they land, and the post-stream
        // attach step below (which requires the last message to already be
        // 'assistant') would silently drop them.
        const ensureAssistantMessage = () => {
          if (firstTokenReceived || isStale()) return;
          firstTokenReceived = true;
          setWaiting(false);
          setMessages((prev) => [...prev, assistantMessage]);
        };

        // The AVA endpoint (or a proxy in front of it) often delivers the answer in a
        // few large network chunks — React batches every token in a chunk into one
        // render, so the whole answer would pop in at once and the spinning mark
        // would barely show. Tokens are queued here instead and revealed on a timer,
        // so the text always types out progressively while `streaming` stays true.
        const revealTick = () => {
          if (isStale()) {
            stopReveal();
            return;
          }
          if (!pendingText) {
            if (streamFinished) stopReveal();
            return;
          }
          // Reveal faster when a big backlog builds up, so a long answer doesn't
          // lag far behind the stream — drains any backlog within ~1s. In a hidden
          // tab the browser throttles this timer (to ~1/s, later ~1/min), and nobody
          // is watching the typing anyway, so show everything queued at once.
          const step =
            typeof document !== 'undefined' && document.hidden
              ? pendingText.length
              : Math.max(REVEAL_MIN_CHARS, Math.ceil(pendingText.length / REVEAL_DRAIN_TICKS));
          const slice = pendingText.slice(0, step);
          pendingText = pendingText.slice(step);
          assistantMessage = { ...assistantMessage, content: assistantMessage.content + slice };
          setMessages((prev) => {
            const next = [...prev];
            if (next[next.length - 1]?.role === 'assistant') next[next.length - 1] = assistantMessage;
            return next;
          });
        };
        revealTimer = setInterval(revealTick, REVEAL_INTERVAL_MS);
        const revealDone = new Promise<void>((resolve) => {
          resolveReveal = resolve;
        });

        await streamNovaAvaChat(
          payload,
          {
            onToken: (token) => {
              if (isStale()) return;
              ensureAssistantMessage();
              pendingText += token;
            },
            onSources: (sources) => {
              if (isStale()) return;
              ensureAssistantMessage();
              latestSources = sources;
            },
            onSuggestedQuestions: (questions) => {
              if (!isStale()) setSuggestedQuestions(questions);
            },
            onImages: (images) => {
              if (isStale()) return;
              ensureAssistantMessage();
              latestImages = images;
            },
            // Merged by citation number in case it's sent in more than one event. Data only —
            // it doesn't open the assistant bubble; it's applied once the stream has ended.
            onCitations: (citations) => {
              if (isStale()) return;
              const byNumber = new Map((latestCitations ?? []).map((c) => [c.citation_number, c]));
              citations.forEach((c) => byNumber.set(c.citation_number, c));
              latestCitations = Array.from(byNumber.values());
            },
          },
          { signal: abortController.signal },
        );
        resolveCitations();

        // Keep `streaming` on (mark spinning) until the queued text has fully typed out.
        // Run a tick right away rather than waiting for the next (possibly throttled)
        // interval — in a hidden tab this flushes everything and resolves immediately,
        // so the save, source attachment and composer unlock aren't held up.
        streamFinished = true;
        revealTick();
        if (!pendingText) revealTick();
        await revealDone;

        // The conversation was switched away from mid-stream — the callbacks above
        // already stopped touching `messages`/`messagesRef`, and this abandoned
        // turn must not merge sources/images into or auto-save the new conversation.
        if (isStale()) return;

        if (abortController.signal.aborted) {
          finishStoppedTurn();
          return;
        }

        if (firstTokenReceived && (latestSources || latestImages)) {
          assistantMessage = {
            ...assistantMessage,
            sources: latestSources ?? assistantMessage.sources,
            images: latestImages ?? assistantMessage.images,
          };
          setMessages((prev) => {
            const next = [...prev];
            if (next[next.length - 1]?.role === 'assistant') next[next.length - 1] = assistantMessage;
            return next;
          });
        }

        // A full answer landed — save the conversation and refresh history right away
        // rather than waiting for an explicit save action.
        if (firstTokenReceived) saveTurn();
      } catch (error) {
        if (isNovaStreamAbort(error) && abortController.signal.aborted) {
          finishStoppedTurn();
          return;
        }
        resolveCitations();
        // Show whatever had arrived but not yet typed out, rather than dropping it.
        stopReveal();
        if (pendingText) {
          assistantMessage = { ...assistantMessage, content: assistantMessage.content + pendingText };
          pendingText = '';
        }
        console.error('Error fetching Nova chat response:', error);
        if (firstTokenReceived && !isStale()) {
          // The stream dropped after part of the answer already arrived — make that
          // visible instead of leaving a silently truncated response with no
          // follow-up suggestions and no indication anything went wrong. Also still
          // attach any sources that arrived before the drop, otherwise they'd be
          // discarded even though the message keeps citations linking to them.
          assistantMessage = {
            ...assistantMessage,
            content: `${assistantMessage.content}\n\n*Response interrupted — please try again.*`,
            sources: latestSources ?? assistantMessage.sources,
            images: latestImages ?? assistantMessage.images,
          };
          messagesRef.current = [...messagesRef.current, assistantMessage];
          setMessages((prev) => {
            const next = [...prev];
            if (next[next.length - 1]?.role === 'assistant') next[next.length - 1] = assistantMessage;
            return next;
          });
        }
        if (!isStale()) showNotification('Nova is having trouble responding right now. Please try again.', 'error');
      } finally {
        stopReveal();
        abortController.signal.removeEventListener('abort', stopReveal);
        if (abortControllerRef.current === abortController) abortControllerRef.current = null;
        // Don't clear these if a newer send/switch has already taken over — they'd
        // wrongly clobber that newer turn's own loading state and re-enable a send
        // that's meant to stay blocked until it (not this abandoned one) finishes.
        if (!isStale()) {
          setWaiting(false);
          setStreaming(false);
          isSendingRef.current = false;
        }
      }
    },
    [
      appInsights,
      user,
      showNotification,
      published12mFilter,
      pendingFilePaths,
      ensureChatSessionKey,
      upsertConversation,
      abortInFlightStream,
    ],
  );

  const startNewConversation = useCallback(() => {
    // Invalidates any in-flight sendMessage from the conversation being left, and
    // frees up sending/loading state for this new one rather than waiting on
    // whatever the abandoned call's own (now-guarded, no-op) finally block does.
    generationRef.current += 1;
    abortInFlightStream();
    isSendingRef.current = false;
    restoredRef.current = true;
    conversationId.current = '';
    chatSessionKey.current = '';
    messagesRef.current = [];
    setWaiting(false);
    setStreaming(false);
    setMessages([]);
    setSuggestedQuestions([]);
    // Pending attachments were meant for the conversation being left — don't carry
    // them into a new, unrelated one.
    setPendingFilePaths([]);
  }, [abortInFlightStream]);

  const resumeConversation = useCallback(
    async (id: string): Promise<boolean> => {
      // Same reasoning as startNewConversation — done before the await so a
      // send still in flight for the conversation being left is invalidated
      // immediately, not only once this fetch resolves.
      generationRef.current += 1;
      const generation = generationRef.current;
      abortInFlightStream();
      isSendingRef.current = false;
      restoredRef.current = true;
      setStreaming(false);
      setWaiting(true);
      try {
        const response = await getNovaConversation(user?.userId || '', id);
        // Image links saved with the chat expire after about an hour — re-sign them first.
        const messages = await refreshNovaImageUrls(response.messages);
        if (generation !== generationRef.current) return true;
        conversationId.current = response.conversationId;
        chatSessionKey.current = '';
        messagesRef.current = messages;
        setMessages(messages);
        setSuggestedQuestions([]);
        setPendingFilePaths([]);
        return true;
      } catch (error) {
        console.error('Error resuming Nova conversation:', error);
        if (generation !== generationRef.current) return true;
        showNotification('Could not load that conversation.', 'error');
        return false;
      } finally {
        if (generation === generationRef.current) setWaiting(false);
      }
    },
    [abortInFlightStream, showNotification, user?.userId],
  );

  const saveToScrapbook = useCallback(async () => {
    const result = await saveToScrapbookWithPath(
      {
        path: `/Nova/nova.${conversationId.current || 'chat'}.json`,
        contentType: ScrapbookContentType.Nova,
        content: JSON.stringify({ pageTitle: 'Nova', messages }),
      },
      user?.userId,
    );

    if (result.succeeded) {
      showNotification(result.message || 'Saved to Scrapbook', 'savesuccess');
    } else {
      showNotification('Failed to save to scrapbook', 'error');
    }
  }, [messages, user?.userId, showNotification]);

  const deleteConversations = useCallback(
    async (ids: string[]) => {
      await deleteNovaConversations(user?.userId || '', ids);
      const remove = (prev: NovaConversationSummary[]) => prev.filter((c) => !ids.includes(c.conversationId));
      setConversations(remove);
      updateCachedConversations(user?.userId || '', remove);
      if (ids.includes(conversationId.current)) {
        startNewConversation();
      }
    },
    [startNewConversation, user?.userId],
  );

  // Restore the remembered chat once, as soon as the user id is known (resuming needs
  // it, and it can arrive late after a token refresh). A stale id — say the chat was
  // deleted elsewhere — is dropped.
  useEffect(() => {
    if (!persistKey || !restoreEnabled || restoredRef.current || !user?.userId) return;
    restoredRef.current = true;
    try {
      const savedId = window.sessionStorage.getItem(persistKey);
      if (!savedId) return;
      resumeConversation(savedId).then((resumed) => {
        if (!resumed) window.sessionStorage.removeItem(persistKey);
      });
    } catch {
      // Storage blocked — start with an empty chat.
    }
  }, [persistKey, restoreEnabled, user?.userId, resumeConversation]);

  // Keep the stored id in step with the active chat, and clear it once the user
  // starts a new one (or deletes the active one) so it isn't restored later.
  const activeConversationId = conversationId.current;
  const hadConversationRef = useRef(false);
  useEffect(() => {
    if (!persistKey) return;
    try {
      if (activeConversationId) {
        hadConversationRef.current = true;
        window.sessionStorage.setItem(persistKey, activeConversationId);
      } else if (hadConversationRef.current) {
        hadConversationRef.current = false;
        window.sessionStorage.removeItem(persistKey);
      }
    } catch {
      // Not persisted — the chat still works for this page view.
    }
  }, [persistKey, activeConversationId]);

  return {
    messages,
    suggestedQuestions,
    waiting,
    streaming,
    conversationId: conversationId.current,
    conversations,
    conversationsLoading,
    published12mFilter,
    setPublished12mFilter,
    pendingFilePaths,
    addPendingFilePaths,
    removePendingFilePath,
    ensureChatSessionKey,
    sendMessage,
    stopStreaming: abortInFlightStream,
    startNewConversation,
    resumeConversation,
    loadConversations,
    deleteConversations,
    saveToScrapbook,
  };
};
